import { useState } from 'react';
import { Button } from '@/components/ui';
import {
  useProposalComments,
  useProposalCommentWriter,
} from '@/hooks/useProposalReviews';
import { useProjectParticipants } from '@/hooks/useProjectViews';
import { ProposalMilestones } from './ProposalMilestones';
import type { ProposalView } from '@/types/current-project';

export function ProposalReviewHistory({
  projectId,
  proposal,
  allowReply = true,
}: {
  projectId: string;
  proposal: ProposalView;
  allowReply?: boolean;
}) {
  const query = useProposalComments(projectId, proposal.proposalId);
  const writer = useProposalCommentWriter(projectId, proposal.proposalId);
  const participants = useProjectParticipants(projectId).data;
  const [message, setMessage] = useState('');
  const author = (account: string) =>
    account === participants?.client.accountId
      ? (participants.client.profiles?.client?.name ??
        participants.client.displayName ??
        'Client')
      : (participants?.coordinator.profiles?.worker?.name ??
        participants?.coordinator.displayName ??
        'Coordinator');
  return (
    <section
      className="backendForm space-y-5 text-left"
      aria-label="Scope review history"
    >
      <h2 className="text-xl font-semibold">Scope review history</h2>
      {query.isPending && <p role="status">Loading responses…</p>}
      {query.isError && (
        <div role="alert">
          <p>{query.error.message}</p>
          <Button onClick={() => void query.refetch()}>Retry responses</Button>
        </div>
      )}
      {query.data?.pages
        .flatMap((page) => page.items)
        .map((item) => (
          <article
            key={item.commentId}
            className="space-y-3 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] p-[--spacing-10]"
          >
            <div className="flex flex-wrap justify-between gap-3">
              <strong>{author(item.authorAccount)}</strong>
              <span className="text-sm text-[--text-dark-secondary]">
                Revision {item.proposalRevision} ·{' '}
                {new Date(item.createdAt * 1000).toLocaleString()}
              </span>
            </div>
            <p className="whitespace-pre-wrap break-words">{item.message}</p>
            <details>
              <summary className="cursor-pointer text-sm text-[--text-dark-secondary]">
                View scope at this response
              </summary>
              <div className="mt-4">
                <p className="mb-4 whitespace-pre-wrap">
                  {item.definition.description}
                </p>
                <ProposalMilestones
                  delivery={item.delivery}
                  definition={item.definition}
                  presentation={item.presentation}
                />
              </div>
            </details>
          </article>
        ))}
      {query.data?.pages[0]?.items.length === 0 && (
        <p className="text-[--text-dark-secondary]">No responses yet.</p>
      )}
      {query.hasNextPage && (
        <Button
          variant="secondary"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          Load more responses
        </Button>
      )}
      {allowReply && writer.error && <p role="alert">{writer.error}</p>}
      {allowReply && writer.pending && (
        <div>
          <p role="status">
            A response is waiting to be saved. Retry only this response.
          </p>
          <Button disabled={writer.busy} onClick={() => void writer.send()}>
            Retry response
          </Button>
        </div>
      )}
      {allowReply && (writer.pending || writer.blocked) && (
        <div className="space-y-3">
          <p className="text-sm text-[--text-dark-secondary]">
            Discarding clears only this local response. Check the history first:
            a response with a lost reply may already be saved. Confirmed project
            decisions remain unchanged.
          </p>
          <Button
            variant="outline"
            disabled={writer.busy}
            onClick={writer.discard}
          >
            Discard local response
          </Button>
        </div>
      )}
      {allowReply && !writer.pending && !writer.blocked && (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void writer.send(message, proposal.revision).then((saved) => {
              if (saved) setMessage('');
            });
          }}
        >
          <label className="block font-semibold">
            Add Comment
            <textarea
              aria-label="Reply to scope review"
              required
              rows={4}
              value={message}
              disabled={writer.busy}
              onChange={(event) => setMessage(event.target.value)}
              className="mt-3 w-full rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] p-3 text-sm font-normal"
            />
          </label>
          <Button type="submit" disabled={writer.busy || !message.trim()}>
            Send response
          </Button>
        </form>
      )}
    </section>
  );
}
